import React from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { normalizeTR } from "@/lib/textFormat";
import { Buildings, Factory, Storefront, Truck, X } from "@phosphor-icons/react";

const TYPE_ICON = { uretici: Factory, bayi: Storefront, tedarikci: Truck };

/**
 * Firma seçmek için yaz-ara tarzı otomatik tamamlama.
 * Kullanıcı yazdıkça, adı yazılanla BAŞLAYAN firmalar dropdown'da listelenir.
 * Eşleşme yoksa `title` (ticari unvan) veya VKN üzerinden de "başlayan" kontrolü yapılır.
 *
 * Props:
 *  - value: string (supplier_id)
 *  - suppliers: [{id, name, title, supplier_type, tax_number, ...}]
 *  - onChange({id, name, ...supplier}): seçim callback'i
 *  - placeholder, testId
 */
export default function SupplierAutocomplete({
  value,
  suppliers = [],
  onChange,
  placeholder = "Firma adı yazın (Örn: De)…",
  testId = "supplier-autocomplete",
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Seçili firmanın adını göster (input value'su)
  const selected = React.useMemo(
    () => (value ? suppliers.find(s => s.id === value) : null),
    [value, suppliers]
  );
  const displayValue = query !== "" ? query : (selected?.name || "");

  const filtered = React.useMemo(() => {
    const q = normalizeTR(query.trim());
    if (!q) {
      // Query boşsa: seçili yoksa hepsini göster (ilk 20), varsa hiçbir şey
      return selected ? [] : suppliers.slice(0, 30);
    }
    // "başlayan" eşleşmesi
    return suppliers.filter(s => {
      const n = normalizeTR(s.name || "");
      const t = normalizeTR(s.title || "");
      const vkn = normalizeTR(s.tax_number || "");
      return n.startsWith(q) || t.startsWith(q) || vkn.startsWith(q);
    }).slice(0, 30);
  }, [query, suppliers, selected]);

  const commitSelection = (s) => {
    onChange?.(s);
    setQuery("");
    setOpen(false);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    setQuery("");
    onChange?.(null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex items-center">
          <Input
            value={displayValue}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            data-testid={testId}
            className="rounded-none sm:rounded-sm pr-9"
          />
          {selected && (
            <button
              type="button"
              onClick={clearSelection}
              className="absolute right-2 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              title="Seçimi temizle"
              data-testid={`${testId}-clear`}
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[--radix-popover-trigger-width] max-h-72 overflow-y-auto rounded-none sm:rounded-sm"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-slate-500 text-center">
            {query.trim()
              ? `"${query}" ile başlayan firma yok`
              : "Firma listesi boş"}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid={`${testId}-list`}>
            {filtered.map((s) => {
              const Icon = TYPE_ICON[s.supplier_type] || Buildings;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => commitSelection(s)}
                    data-testid={`${testId}-item-${s.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F7FBE9] flex items-center gap-2"
                  >
                    <Icon size={14} weight="duotone" className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 font-bold truncate">{s.name}</div>
                      {(s.title || s.tax_number) && (
                        <div className="text-[10px] text-slate-500 truncate">
                          {s.title || ""}{s.title && s.tax_number ? " · " : ""}
                          {s.tax_number && <span className="font-mono">VKN: {s.tax_number}</span>}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
